/* eslint-disable jsx-a11y/no-autofocus */
// External Dependencies
import React, { useState, useRef, useEffect, useReducer } from 'react';
import { toast } from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import Spinner from 'sushiweb/Spinner/Spinner';

// Internal Dependencies
import { InvalidSearchImg } from '../../../../images/images';
import { useMutationCall, useQueryCall } from '../../../../api/react-client';
import { EmptyState } from '../../../../components/commons';

// UI Components
import { Textarea } from '../../../../UiKit';
import Shimmer from 'sushiweb/Shimmer';
import { getHeaderWithTooltip } from '../bulk-fraud-marking/HeaderTooltip';
import { UserRoundCheck } from 'lucide-react';
import TabHeader from '../../Components/TabHeader/TabHeader';
import MultiAddInputV1 from '../bulk-fraud-marking/MultiAddInputV1';

const UNMARK_FRAUD_ENDPOINT = 'user/bulk_unmark_fraud';

const multiSelectStyles = `
                          .option:hover {
                                background-color: #318616 !important;
                           }
                          .searchWrapper {
                                background-color: white !important;
                          }
                         `;

const INITIAL_PAYLOAD_STATE = {
  parsedIdentifiers: [],
  reasons: [],
  comment: '',
  entityTypes: [],
};

const ACTIONS = {
  SET_IDENTIFIERS: 'set_identifiers',
  SET_REASONS: 'set_reasons',
  SET_COMMENT: 'set_comment',
  SET_ENTITY_TYPES: 'set_entity_types',
  RESET: 'reset',
};

const reducerFunction = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_IDENTIFIERS:
      return {
        ...state,
        parsedIdentifiers: action.payload,
      };
    case ACTIONS.SET_REASONS:
      return {
        ...state,
        reasons: action.payload,
      };
    case ACTIONS.SET_COMMENT:
      return {
        ...state,
        comment: action.payload,
      };
    case ACTIONS.SET_ENTITY_TYPES:
      return {
        ...state,
        entityTypes: action.payload,
      };
    case ACTIONS.RESET:
      return INITIAL_PAYLOAD_STATE;
    default:
      return state;
  }
};

const UnmarkFraud = () => {
  const multiselectEntityTypesRef = useRef();
  const multiselectReasonsRef = useRef();

  // helper states
  const [rawIdentifiersInput, setRawIdentifiersInput] = useState('');
  const [reasonList, setReasonList] = useState([]);
  const [entityTypesList, setEntityTypesList] = useState([]);

  // form payload reducer
  const [state, dispatch] = useReducer(reducerFunction, INITIAL_PAYLOAD_STATE);
  const { parsedIdentifiers, reasons, comment, entityTypes } = state || {};

  const {
    data: fraudParams,
    error: paramsError,
    isLoading: isParamsLoading,
    isError: isParamsError,
  } = useQueryCall(
    {
      onSuccess: (response) => {
        // Check for nested data structure
        if (!response?.reason_options) {
          toast.error('Invalid fraud parameters received');
          return;
        }

        // Transform primary reasons into correct format for Multiselect
        const reasonsList = response.reason_options?.map((reason) => ({
          id: reason?.value,
          name: reason?.label,
        }));
        setReasonList(reasonsList);

        // Transform entity types into correct format for Multiselect
        const entityOptions = response.entity_types?.map((entity_type) => ({
          id: entity_type?.value,
          name: entity_type?.label,
        }));
        setEntityTypesList(entityOptions);
      },
    },
    UNMARK_FRAUD_ENDPOINT,
    ['getFraudParams'],
  );

  const resetForm = () => {
    dispatch({ type: ACTIONS.RESET });
    setRawIdentifiersInput('');
    // Reset Entity Types Multiselect
    if (
      multiselectEntityTypesRef.current &&
      multiselectEntityTypesRef.current.resetSelectedValues
    ) {
      try {
        multiselectEntityTypesRef.current.resetSelectedValues();
      } catch (e) {
        // Fail silently if not supported
      }
    }
    // Reset Reasons Multiselect
    if (
      multiselectReasonsRef.current &&
      multiselectReasonsRef.current.resetSelectedValues
    ) {
      try {
        multiselectReasonsRef.current.resetSelectedValues();
      } catch (e) {
        // Fail silently if not supported
      }
    }
  };

  //Unmark Fraud
  const { mutate: unmarkFraud, isLoading: isMarking } = useMutationCall(
    {
      onSuccess: (data) => {
        if (data) {
          if (
            data.failed_identifiers?.length > 0 ||
            Object.keys(data.failed_actions || {}).length > 0
          ) {
            toast.error(
              'Unmarked successfully, but some entities failed to process.',
            );
          } else {
            toast.success('Unmarked successfully');
          }
        }
        resetForm();
      },
    },
    UNMARK_FRAUD_ENDPOINT,
    [],
    {
      doNotShowSuccessToast: true,
    },
  );

  const handleEntityTypeUpdate = (selectedList) => {
    dispatch({ type: ACTIONS.SET_ENTITY_TYPES, payload: selectedList || [] });
  };

  const handleReasonUpdate = (selectedList) => {
    dispatch({ type: ACTIONS.SET_REASONS, payload: selectedList || [] });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (parsedIdentifiers?.length === 0) {
      toast.error('Please provide at least one identifier');
    } else if (!entityTypes?.length) {
      toast.error('Select at least one entity type');
    } else if (!reasons?.length) {
      toast.error('Select at least one reason');
    } else {
      const dataToSubmit = {
        reason_codes: reasons?.map((entity) => entity?.id) || [],
        comment: comment,
        entity_types: entityTypes?.map((entity) => entity?.id) || [],
        identifiers: parsedIdentifiers || [],
      };
      unmarkFraud(dataToSubmit);
    }
  };

  const handleAddIdentifiers = (inputValue, setInputValue, setParsedValues) => {
    const trimmedInputValue = (inputValue ?? '').trim();
    if (trimmedInputValue === '') {
      toast.error(`Please enter a valid value`);
      return;
    }

    const parsedValues = trimmedInputValue
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '');

    if (parsedValues.length > 0) {
      setInputValue('');
      setParsedValues([...new Set([...parsedIdentifiers, ...parsedValues])]);
    }
  };

  const handleRemoveIdentifier = (setterFunction, value) => {
    const updatedIdentifiersArray = parsedIdentifiers.filter(
      (ident) => ident !== value,
    );
    setterFunction(updatedIdentifiersArray);
  };

  useEffect(() => {
    const form = document.querySelector('form');
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
        e.preventDefault();
      }
    };

    if (form) {
      form.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (form) {
        form.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  if (isParamsError) {
    return (
      <div className='tw-w-full'>
        <EmptyState
          img={InvalidSearchImg}
          heading={paramsError?.message || 'Something went wrong!'}
          isPop={true}
        />
      </div>
    );
  }

  if (isParamsLoading) return <Shimmer height='20rem' width={'100%'} />;

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-gap-2'>
      <TabHeader
        title='Bulk Fraud Unmarking'
        icon={
          <UserRoundCheck size={20} strokeWidth={2.4} className='tw-ml-1' />
        }
        subTitle='Remove the fraud status from one or more identifiers.'
      />

      {fraudParams && (
        <form onSubmit={handleSubmit} className='tw-pb-20 tw-mt-12'>
          <div className='tw-flex-col tw-gap-10'>
            {/* Identifier Input field */}
            <MultiAddInputV1
              // Label and input placeholders
              label='Identifiers (Customer Id, Payment Id, Device Id)'
              placeholder='Enter value(s) and press Enter'
              parsedLabel='Parsed Identifiers: '
              // raw input value and setter
              rawValue={rawIdentifiersInput}
              setRawValue={setRawIdentifiersInput}
              // Array of parsed values and setter
              parsedValuesArray={parsedIdentifiers}
              setParsedValues={(values) =>
                dispatch({ type: ACTIONS.SET_IDENTIFIERS, payload: values })
              }
              // Handler to add an identifier on Enter or plus button click
              handleAddValue={() =>
                handleAddIdentifiers(
                  rawIdentifiersInput,
                  setRawIdentifiersInput,
                  (values) =>
                    dispatch({
                      type: ACTIONS.SET_IDENTIFIERS,
                      payload: values,
                    }),
                )
              }
              // Handler to remove a single parsed identifier chip
              handleRemoveParsedValue={handleRemoveIdentifier}
            />

            {/* Entity Types Selection Dropdown */}
            {entityTypesList?.length > 0 ? (
              <label htmlFor='entityTypes' css={multiSelectStyles}>
                {getHeaderWithTooltip(
                  'Entity Type',
                  'Select the type of entity you want to unmark',
                )}
                <Multiselect
                  ref={multiselectEntityTypesRef}
                  avoidHighlightFirstOption
                  options={entityTypesList || []}
                  selectedValues={entityTypes}
                  onSelect={handleEntityTypeUpdate}
                  onRemove={handleEntityTypeUpdate}
                  displayValue='name'
                  placeholder='Search entity types...'
                />
              </label>
            ) : null}

            {/* Reasons */}
            <label htmlFor='primaryReasons' css={multiSelectStyles}>
              {getHeaderWithTooltip('Reasons')}
              <Multiselect
                ref={multiselectReasonsRef}
                avoidHighlightFirstOption
                options={reasonList}
                selectedValues={reasons}
                onSelect={handleReasonUpdate}
                onRemove={handleReasonUpdate}
                displayValue='name'
                placeholder='Search reasons...'
              />
            </label>

            {/* Comments Section */}
            <label htmlFor='Comment'>
              {getHeaderWithTooltip(
                'Remarks',
                'Add any extra information explaining why this should be unmarked',
              )}
              <Textarea
                placeholder='Add some remarks'
                value={comment}
                onChange={(e) => {
                  dispatch({
                    type: ACTIONS.SET_COMMENT,
                    payload: e.target.value,
                  });
                }}
                maxLimit='300'
                showBtn={false}
              />
            </label>
          </div>

          {/* Submit Button */}
          <div
            css={`
              margin-top: 3rem;
              display: flex;
              justify-content: flex-end;
            `}
          >
            <button
              type='submit'
              disabled={isMarking || isParamsLoading}
              className='tw-text-[14px] tw-font-medium tw-bg-[#318616] tw-text-white tw-rounded-[0.6rem] tw-border-none tw-px-12 tw-py-4 tw-min-h-8 tw-leading-none tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-bg-[#cfcfcf]'
            >
              {isMarking ? (
                <div className='tw-w-full tw-justify-center tw-items-center tw-text-white'>
                  <Spinner color={'white'} />
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default UnmarkFraud;
